import React, { useEffect, useMemo, useState } from "react";
import { Check, Loader2, Plus, Trash2 } from "lucide-react";

import {
  createBrandProfileV2,
  getBrandProfileV2,
  updateBrandProfileV2,
} from "../../lib/brandStudioApi";

const TYPES = [
  ["instagram", "Instagram"],
  ["telegram", "Telegram"],
  ["whatsapp", "WhatsApp"],
  ["phone", "Телефон"],
  ["email", "Email"],
  ["website", "Сайт"],
  ["other", "Другое"],
];

const nextChannel = (order) => ({
  id: crypto.randomUUID(),
  type: "instagram",
  value: "",
  label: "",
  order,
  enabled: true,
});

const normalize = (items = []) =>
  items.map((item, index) => ({
    id: item.id || crypto.randomUUID(),
    type: item.type || "other",
    value: item.value || "",
    label: item.label || "",
    order: index,
    enabled: item.enabled !== false,
  }));

export default function BrandBusinessDataStepV2({ onContinue = null }) {
  const [status, setStatus] = useState("loading");
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState("");
  const [channels, setChannels] = useState([]);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setStatus("loading");
    setError("");
    try {
      const current = await getBrandProfileV2();
      setProfile(current || null);
      setName(current?.brand_name || "");
      setChannels(normalize(current?.contact_channels || []));
      setSaved(Boolean(current?.brand_name));
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const preparedChannels = useMemo(
    () =>
      channels
        .filter((item) => item.value.trim())
        .map((item, index) => ({
          id: item.id,
          type: item.type,
          value: item.value.trim(),
          ...(item.type === "other" && item.label.trim()
            ? { label: item.label.trim() }
            : {}),
          order: index,
          enabled: true,
        }))
        .filter((item) => item.type !== "other" || item.label),
    [channels],
  );

  const save = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const payload = {
        brand_name: name.trim(),
        contact_channels: preparedChannels,
      };
      const next = profile
        ? await updateBrandProfileV2(payload)
        : await createBrandProfileV2({ ...payload, brand_brief: {} });
      setProfile(next);
      setName(next.brand_name || "");
      setChannels(normalize(next.contact_channels || []));
      setSaved(true);
    } catch {
      setError("Не удалось сохранить данные бизнеса. Всё введённое осталось на экране.");
    } finally {
      setBusy(false);
    }
  };

  const patch = (id, values) => {
    setSaved(false);
    setChannels((previous) =>
      previous.map((item) => (item.id === id ? { ...item, ...values } : item)),
    );
  };

  if (status === "loading") {
    return (
      <section className="rounded-[22px] border border-[#E1D4C2] bg-[#FFFDF9] p-5 md:p-7">
        <div className="inline-flex items-center gap-2 text-sm text-[#796652]">
          <Loader2 size={16} className="animate-spin" /> Открываю данные бизнеса…
        </div>
      </section>
    );
  }

  if (status === "error") {
    return (
      <section className="rounded-[22px] border border-[#E2CFC8] bg-[#FFF8F4] p-5 md:p-7">
        <h2 className="font-serif text-[28px] text-[#2B211A]">Не удалось открыть данные бизнеса</h2>
        <button
          type="button"
          onClick={load}
          className="mt-4 min-h-11 rounded-full border border-[#CDB899] bg-white px-5 text-sm text-[#4A3728]"
        >
          Повторить
        </button>
      </section>
    );
  }

  return (
    <section
      className="rounded-[22px] border border-[#E1D4C2] bg-[#FFFDF9] p-5 md:p-7"
      data-testid="brand-business-data-v2"
    >
      <div className="text-[9.5px] uppercase tracking-[0.22em] text-[#8A6D53]">ДАННЫЕ БИЗНЕСА</div>
      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="font-serif text-[31px] leading-none text-[#2B211A] md:text-[36px]">
            То, что должно быть написано точно
          </h2>
          <p className="mt-3 max-w-2xl text-[13.5px] leading-6 text-[#655141]">
            Сейчас достаточно названия бренда и способов связи. Остальное можно добавить позже.
          </p>
        </div>
        {saved && (
          <div className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[12px] text-[#586044]">
            <Check size={14} /> Сохранено
          </div>
        )}
      </div>

      <label className="mt-6 block">
        <span className="text-[11px] uppercase tracking-[0.14em] text-[#927A5B]">Название бренда</span>
        <input
          value={name}
          maxLength={120}
          onChange={(event) => {
            setName(event.target.value);
            setSaved(false);
          }}
          className="mt-1.5 min-h-12 w-full rounded-xl border border-[#D8C7AE] bg-white px-4 text-[14px] text-[#3E2E20] outline-none focus:border-[#8C6C49]"
          data-testid="brand-business-name"
        />
      </label>

      <div className="mt-6 border-t border-[#EEE3D5] pt-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-serif text-[25px] text-[#2B211A]">Как с тобой связаться</h3>
            <p className="mt-1 text-[12.5px] leading-5 text-[#806B55]">
              Добавляй только те контакты, которые должны использоваться в материалах.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setSaved(false);
              setChannels((previous) => [...previous, nextChannel(previous.length)]);
            }}
            className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[#D2C1AA] bg-white px-4 text-[12.5px] text-[#4A3728]"
            data-testid="brand-business-add-channel"
          >
            <Plus size={14} /> Добавить контакт
          </button>
        </div>

        <div className="mt-4 space-y-3">
          {channels.map((item) => (
            <div
              key={item.id}
              className="grid gap-2 rounded-[14px] border border-[#E4D7C6] bg-[#FAF6EF] p-3 md:grid-cols-[160px_minmax(0,1fr)_auto]"
            >
              <select
                value={item.type}
                onChange={(event) =>
                  patch(item.id, {
                    type: event.target.value,
                    ...(event.target.value !== "other" ? { label: "" } : {}),
                  })
                }
                className="min-h-11 rounded-xl border border-[#D8C7AE] bg-white px-3 text-[13px] text-[#3E2E20]"
              >
                {TYPES.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>

              <div className="grid gap-2">
                {item.type === "other" && (
                  <input
                    value={item.label}
                    maxLength={80}
                    onChange={(event) => patch(item.id, { label: event.target.value })}
                    placeholder="Название контакта"
                    className="min-h-11 rounded-xl border border-[#D8C7AE] bg-white px-3 text-[13px] text-[#3E2E20]"
                  />
                )}
                <input
                  value={item.value}
                  maxLength={300}
                  onChange={(event) => patch(item.id, { value: event.target.value })}
                  placeholder="Контакт или ссылка"
                  className="min-h-11 rounded-xl border border-[#D8C7AE] bg-white px-3 text-[13px] text-[#3E2E20]"
                />
              </div>

              <button
                type="button"
                onClick={() => {
                  setSaved(false);
                  setChannels((previous) =>
                    previous
                      .filter((channel) => channel.id !== item.id)
                      .map((channel, index) => ({ ...channel, order: index })),
                  );
                }}
                className="inline-flex min-h-11 items-center justify-center gap-1.5 px-3 text-[12px] text-[#7E5145]"
              >
                <Trash2 size={14} /> Убрать
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <button
          type="button"
          disabled={!name.trim() || busy}
          onClick={save}
          className="min-h-12 rounded-full bg-[#3D2A20] px-6 text-sm text-[#FFF8ED] disabled:opacity-45"
          data-testid="brand-business-save"
        >
          {busy ? "Сохраняю…" : "Сохранить данные"}
        </button>
        {saved && onContinue && (
          <button
            type="button"
            onClick={onContinue}
            className="min-h-12 rounded-full border border-[#D2C1AA] bg-white px-5 text-sm text-[#4A3728]"
            data-testid="brand-business-continue"
          >
            Продолжить
          </button>
        )}
      </div>

      {error && (
        <div className="mt-4 rounded-xl border border-[#E2CFC8] bg-[#FFF8F4] p-4 text-[13px] leading-5 text-[#6B443A]">
          {error}
        </div>
      )}
    </section>
  );
}
